import { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Tabs, Modal, Button } from '@ets/design-system';
import { isEqual } from 'lodash-es';

import EditorManager from 'Editor/services/EditorManager';
import { useDispatch, useSelector, useTabsState } from '_common/hooks';
import { closeModal } from '_common/modals/ModalsSlice';

import { Cell, Column, Row, Table } from './TabContent';
import TablePropertiesContext, { AbsoluteUnit } from './TablePropertiesContext';

import styles from './TableProperties.module.scss';
import { IntlErrorBoundary } from '_common/components';

const TableProperties = () => {
  const intl = useIntl();
  const dispatch = useDispatch();

  const [preferedUnit, setPreferedUnit] = useState<AbsoluteUnit>('cm');
  const [hasValidWidth, setHasValidWidth] = useState(true);
  const [originalData, setOriginalData] = useState<Editor.Edition.TablePropertiesData | null>(null);
  const [unsavedData, setUnsavedData] = useState<Editor.Edition.TablePropertiesData>({
    TABLE: {},
    ROW: {},
    COLUMN: {},
    CELL: {},
  });

  const isOpen = useSelector((state) => state.modals.open.TablePropertiesModal);
  const { tab, onTabChange } = useTabsState('tableProperties');

  useEffect(() => {
    if (isOpen) {
      const manager = EditorManager.getInstance();
      const tableProperties = manager.getSelectedTableProperties();
      if (tableProperties) {
        setOriginalData(tableProperties);
        setUnsavedData(tableProperties);
      }
    }
  }, [isOpen]);

  const handleClose = () => {
    EditorManager.getInstance().restore();
    dispatch(closeModal('TablePropertiesModal'));
  };

  const handleUpdateData: TabContentProps['updateData'] = ({ element, property, value }) => {
    setUnsavedData((prevState) => {
      let newState = {
        ...prevState,
        [element]: {
          ...prevState[element],
          [property]: value,
        },
      };

      return newState;
    });
  };

  const filterDiffProperties = (
    elementUnsavedData: Editor.Edition.TableProperties,
    elementOriginalData: Editor.Edition.TableProperties | undefined,
  ): Partial<Editor.Edition.TableProperties> => {
    if (!elementOriginalData) {
      return {};
    }

    let diffProperties: Partial<Editor.Edition.TableProperties> = {};

    Object.keys(elementUnsavedData).forEach((property) => {
      const typedProperty = property as keyof typeof elementUnsavedData;
      if (!isEqual(elementUnsavedData[typedProperty], elementOriginalData[typedProperty])) {
        diffProperties = {
          ...diffProperties,
          [typedProperty]: elementUnsavedData[typedProperty],
        };
      }
    });

    return diffProperties;
  };

  const handleSave = () => {
    const diff: Editor.Edition.TablePropertiesData = {
      TABLE: filterDiffProperties(unsavedData.TABLE, originalData?.TABLE),
      ROW: filterDiffProperties(unsavedData.ROW, originalData?.ROW),
      COLUMN: filterDiffProperties(unsavedData.COLUMN, originalData?.COLUMN),
      CELL: filterDiffProperties(unsavedData.CELL, originalData?.CELL),
    };

    EditorManager.getInstance().setTableProperties(diff);
    handleClose();
  };

  if (!originalData) {
    return null;
  }

  return (
    <Modal open={!!isOpen} close={handleClose} width={'112rem'} testId="table-properties">
      <Modal.Header>
        <FormattedMessage id="TABLE_PROPERTIES" />
      </Modal.Header>
      <Modal.Body style={{ display: 'flex' }}>
        <TablePropertiesContext.Provider
          value={{
            pageSize: originalData.PAGE?.width,
            setHasValidWidth,
            preferedUnit,
            setPreferedUnit,
          }}
        >
          <Tabs
            value={tab || 'TABLE'}
            onValueChange={onTabChange}
            style={{ display: 'flex', flexDirection: 'column' }}
          >
            <Tabs.List>
              <Tabs.Trigger value="TABLE">{intl.formatMessage({ id: 'TABLE' })}</Tabs.Trigger>
              <Tabs.Trigger value="COLUMN">{intl.formatMessage({ id: 'COLUMN' })}</Tabs.Trigger>
              <Tabs.Trigger value="ROW">{intl.formatMessage({ id: 'ROW' })}</Tabs.Trigger>
              <Tabs.Trigger value="CELL">{intl.formatMessage({ id: 'CELL' })}</Tabs.Trigger>
            </Tabs.List>
            <Tabs.Content value="TABLE" className={styles.contentContainer}>
              <IntlErrorBoundary key="TABLE" size="xl" style={{ margin: '5rem 0' }}>
                <Table unsavedData={unsavedData.TABLE} updateData={handleUpdateData} />
              </IntlErrorBoundary>
            </Tabs.Content>
            <Tabs.Content value="COLUMN" className={styles.contentContainer}>
              <IntlErrorBoundary key="COLUMN" size="xl" style={{ margin: '5rem 0' }}>
                <Column unsavedData={unsavedData.COLUMN} updateData={handleUpdateData} />
              </IntlErrorBoundary>
            </Tabs.Content>
            <Tabs.Content value="ROW" className={styles.contentContainer}>
              <IntlErrorBoundary key="ROW" size="xl" style={{ margin: '5rem 0' }}>
                <Row unsavedData={unsavedData.ROW} updateData={handleUpdateData} />
              </IntlErrorBoundary>
            </Tabs.Content>
            <Tabs.Content value="CELL" className={styles.contentContainer}>
              <IntlErrorBoundary key="CELL" size="xl" style={{ margin: '5rem 0' }}>
                <Cell unsavedData={unsavedData.CELL} updateData={handleUpdateData} />
              </IntlErrorBoundary>
            </Tabs.Content>
          </Tabs>
        </TablePropertiesContext.Provider>
      </Modal.Body>
      <Modal.Footer>
        <Button size="m" variant="standard" onClick={handleClose} testId="table-properties-cancel">
          <FormattedMessage id="global.cancel" />
        </Button>
        <Button
          size="m"
          style={{ margin: '0 0 0 1rem' }}
          variant="primary"
          disabled={!hasValidWidth}
          onClick={handleSave}
          testId="table-properties-submit"
        >
          <FormattedMessage id="global.save" />
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default TableProperties;
